import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-request-your-fostering-records';

export const RequestFosteringRecords = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        If you lived with foster carers, there is usually more than one organisation holding
        records about the placement. The local authority (council) that placed you holds its
        own file, and if an independent fostering agency was involved in finding or supervising
        the carer, it holds a separate one. Both are legally separate data controllers, and you
        have the right to ask each of them for what they hold, under Article 15 of the UK GDPR.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Who to ask" title="Council, agency, or both?">
      <GuideChecklist
        items={[
          'The council that had responsibility for you (see our guide on requesting care records from the council) usually holds the wider case file — why you were placed, reviews of the placement, and decisions made about your care.',
          "An independent fostering agency (IFA), if one was involved in recruiting, training or supervising your foster carer, holds its own file on the carer and the placement — including matching notes and the agency's own visit records.",
          "If you don't know whether an agency was involved, it's worth asking the council directly — they will know, and their answer doesn't cost you anything to ask for.",
        ]}
      />
      <p>
        It is common, and worthwhile, to send a request to both. They rarely hold identical
        material — an agency&apos;s day-to-day supervising social worker notes, for example, may
        exist only in the agency&apos;s own file, not the council&apos;s.
      </p>
    </GuideSection>

    <GuideSection eyebrow="What to ask for" title="What a fostering record usually contains">
      <GuideChecklist
        items={[
          'Matching and placement records — why you were placed with that particular carer.',
          "Supervising social worker visit notes, usually made regularly throughout a placement.",
          'Foster carer reviews and any annual reviews of the placement.',
          'Placement agreements and placement plans.',
          "Daily or weekly logs the carer or agency kept about you, where they exist.",
          'Any records of a placement disruption or move, and the reasons recorded for it.',
          'Records of any allegations, complaints or safeguarding concerns connected to the placement.',
        ]}
      />
    </GuideSection>

    <GuideNote>
      <p>
        <strong>Several placements, several requests.</strong> If you had more than one foster
        placement, especially through different agencies, you may need a separate request for
        each one. It is worth listing every placement you remember, even approximately — a
        rough year and a first name is often enough for an organisation to start a search.
      </p>
    </GuideNote>

    <GuideCTA />

    <GuideSection eyebrow="What happens next" title="Deadlines and what you might get back">
      <p>
        Both a council and a fostering agency have{' '}
        <strong className="text-ink">one calendar month</strong> to respond, extendable by up to
        two further months if the request is genuinely complex. Fostering records sometimes
        arrive with the foster carer&apos;s own personal details redacted — their home address, for
        instance — since that is the carer&apos;s data, not yours. What should not be redacted is
        anything the carer wrote or reported about you, or decisions made about your placement.
      </p>
      <p>
        If a fostering agency has since closed down, its records don&apos;t disappear — they should
        have been transferred to the council or to whoever took over its regulated activity.
        Ask the relevant council who now holds them if you can&apos;t find the agency itself.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="fostering-records-faq">
        <GuideFaqItem
          q="Can I get my foster carer's contact details from this?"
          a="Not automatically — an organisation isn't obliged to hand over another individual's personal data, including a former carer's current address or phone number, as part of your SAR. If you want to reconnect with a former carer, that's usually a separate, more sensitive conversation, and some local authorities or agencies can help facilitate contact if both people are willing."
        />
        <GuideFaqItem
          q="What about short breaks or respite carers?"
          a="Respite or short-break carers who looked after you occasionally, alongside a main placement, are usually recorded by whichever council or agency arranged the main placement — ask them specifically about any respite or short-break care you remember, by name if you have one."
        />
        <GuideFaqItem
          q="I don't remember the name of the fostering agency. What do I do?"
          a="The council that placed you will know, even if you don't — ask them directly as part of your request to the council, or as a follow-up question. You don't need the agency's name to start."
        />
        <GuideFaqItem
          q="Is a private fostering arrangement covered the same way?"
          a="If a council was ever notified of a private fostering arrangement, it should hold some record of it, though usually far less detailed than a formal foster placement. It's still worth asking — your right of access applies to whatever they do hold."
        />
        <GuideFaqItem
          q="Can I send a request straight to my former foster carer instead?"
          a={
            <>
              It isn&apos;t a reliable route, even though it feels like the direct one. See our
              guide on{' '}
              <Link to="/guides/can-you-send-a-sar-to-a-foster-carer" className="text-brand underline hover:opacity-90">
                whether you can send a Subject Access Request to a foster carer
              </Link>{' '}
              for why — and what to ask the council or agency for instead.
            </>
          }
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The legal basis is the same as any SAR: Article 15 of the{' '}
        <SourceLink href="https://www.legislation.gov.uk/eur/2016/679/article/15">UK GDPR</SourceLink>{' '}
        and the{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/contents">
          Data Protection Act 2018
        </SourceLink>
        . For the general mechanics — deadlines, ID checks, what counts as a reasonable fee — see
        the ICO&apos;s{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/">
          right of access guidance
        </SourceLink>
        , or our own{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          plain-English guide to SARs
        </Link>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="Add every placement, let us chase every organisation"
      body="Add your council and any fostering agency you remember, and we'll draft separate letters, send them, and track each deadline so nothing quietly falls off your list."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RequestFosteringRecords;
